import React, { useEffect } from "react";
import { Button } from "@heroui/react";
import { useAuth, useUser } from "@webuild-ai/react-auth";
import { useBrand } from "@webuild-ai/react-auth";
import { detectPermissionTamperingWithServer as detectPermissionTampering } from "@webuild-ai/react-auth";

import { ModalOverlay, ModalHeader, ModalBody } from "@/components/UI/ModalOverlay";
import { useWebuildAPIClient } from "@/providers/WeBuildClientProvider";
import { useTheme } from "@/hooks/use-theme";
import { logger } from "@/telemetry";

/**
 * Component that monitors for permission tampering and alerts the user
 */
/**
 * Determine if an error is security-related or a transient system error
 */
const isSecurityError = (error: any): boolean => {
  if (!error) return false;

  const errorMessage = error?.message?.toLowerCase() || "";
  const errorName = error?.name?.toLowerCase() || "";

  // Security-related errors that should trigger logout
  const securityIndicators = [
    "permission",
    "unauthorized",
    "forbidden",
    "access denied",
    "tampering",
    "security",
    "authentication",
  ];

  // Check if error message or name contains security indicators
  return securityIndicators.some(
    (indicator) => errorMessage.includes(indicator) || errorName.includes(indicator),
  );
};

export const TamperDetection: React.FC = () => {
  const { adminClient } = useWebuildAPIClient();
  const { user, logout } = useAuth();
  const { userProfile } = useUser();
  const { brand } = useBrand();
  const { theme } = useTheme();
  const [showTamperAlert, setShowTamperAlert] = React.useState(false);
  const [retryCount, setRetryCount] = React.useState(0);
  const maxRetries = 2;

  // Get brand colors based on current theme
  const brandColors = theme === "dark" ? brand.colors.dark : brand.colors.light;

  useEffect(() => {
    if (!adminClient || !user?.id || !userProfile?.config) {
      return;
    }

    const checkForTampering = async () => {
      try {
        // First check: Compare stored config against fresh server verification
        const tampered = await detectPermissionTampering(
          adminClient,
          user.id,
          userProfile.config as Record<string, boolean>,
        );

        if (tampered) {
          logger.error("SECURITY: Permission tampering detected via config comparison");
          setShowTamperAlert(true);

          return;
        }
      } catch (error) {
        // Handle errors from detectPermissionTampering
        if (isSecurityError(error)) {
          logger.error("SECURITY: Security error during permission verification", error);
          setShowTamperAlert(true);

          return;
        } else {
          logger.warn("Transient error during permission check, will retry", {
            error: String(error),
          });
          // Continue to second check or retry
        }
      }

      // Second check: Verify initial permissions haven't been modified
      // This catches if someone intercepts the initial verify-permissions call
      try {
        const storedCheck = sessionStorage.getItem("_perm_check");

        if (storedCheck) {
          const { perms: storedPerms, userId: storedUserId, timestamp } = JSON.parse(storedCheck);

          // Verify this is for the current user and not stale (< 1 hour old)
          if (storedUserId === user.id && Date.now() - timestamp < 3600000) {
            // Get fresh permissions and compare with what was initially loaded
            const { verifyUserPermissions } = await import("@webuild-ai/react-auth");
            const freshPerms = await verifyUserPermissions(adminClient, user.id);

            // Check if initially loaded permissions match fresh permissions
            const permissionKeys = [
              "workflows",
              "knowledge_hub",
              "prompt_library",
              "monitoring",
              "settings",
            ] as const;

            for (const key of permissionKeys) {
              const freshVal = freshPerms.permissions[key] || false;
              const storedVal = storedPerms[key] || false;

              if (freshVal !== storedVal) {
                logger.error("SECURITY: Initial load tampering detected", {
                  key,
                  storedVal: String(storedVal),
                  freshVal: String(freshVal),
                });
                setShowTamperAlert(true);

                return;
              }
            }
          }
        }
      } catch (error) {
        // Distinguish between security errors and transient errors
        if (isSecurityError(error)) {
          logger.error("SECURITY: Security error during integrity check", error);
          setShowTamperAlert(true);
        } else {
          // Log transient errors but don't force logout
          logger.warn(
            "Integrity check failed due to transient error (network/API issue), not treating as tampering",
            {
              error: String(error),
            },
          );

          // Optionally retry on transient errors
          if (retryCount < maxRetries) {
            logger.warn("Will retry integrity check", { attempt: retryCount + 1, maxRetries });
            setRetryCount(retryCount + 1);
            // Retry after a short delay
            setTimeout(() => checkForTampering(), 2000);
          } else {
            logger.warn(
              "Max retries reached for integrity check. Allowing user to continue but logging the issue.",
            );
          }
        }
      }
    };

    // Check for tampering only once when component mounts
    checkForTampering();
  }, [adminClient, user?.id, userProfile?.config, retryCount]);

  const handleSecurityLogout = () => {
    // Log the security incident (you might want to send this to your monitoring service)
    logger.error("SECURITY: User logged out due to permission tampering detection");

    // Force logout
    logout();
  };

  if (!showTamperAlert) return null;

  return (
    <ModalOverlay isDismissable={false} isOpen={showTamperAlert} size="md">
      <ModalHeader
        className="flex flex-col gap-1 font-bold"
        style={{
          color: brandColors.primary,
          borderBottom: `2px solid ${brandColors.primary}`,
        }}
      >
        Security Alert
      </ModalHeader>
      <ModalBody className="py-6">
        <div className="space-y-4">
          <p className="text-default-700 font-medium">
            A security violation has been detected. Your session permissions have been compromised
            or tampered with.
          </p>
          <p className="text-default-600">
            For your security, you will be logged out immediately. Please log in again to continue
            with validated permissions.
          </p>
          <div className="flex justify-end gap-2 pt-4">
            <Button
              style={{
                backgroundColor: brandColors.primary,
                color: theme === "dark" ? brandColors.secondary : "#ffffff",
              }}
              variant="primary"
              onPress={handleSecurityLogout}
            >
              Logout Now
            </Button>
          </div>
        </div>
      </ModalBody>
    </ModalOverlay>
  );
};
